import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { cartService } from '@/services/cart.service';
import { mediaUrl, formatApiError } from '@/lib/api';
import { HoldCountdown } from '@/components/cart/HoldCountdown';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '@/components/ui/sheet';
import {
  ShoppingCart, Plus, Minus, Trash2, Sparkles, TicketPercent, CreditCard,
  Loader2, LogIn, Leaf, ArrowRight, Compass, Wrench, BedDouble, CalendarDays, Users,
  UtensilsCrossed, Clock, Package, HandHeart,
} from 'lucide-react';

const eur = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(v || 0));
const n = (v) => Number(v || 0).toLocaleString('it-IT');
const fmtD = (iso) => (iso ? iso.split('-').reverse().slice(0, 2).join('/') : '');
const KIND = {
  event: { label: 'Evento', Icon: Sparkles },
  course: { label: 'Corso', Icon: Sparkles },
  rental: { label: 'Noleggio', Icon: Wrench },
  restaurant_deposit: { label: 'Acconto ristorante', Icon: UtensilsCrossed },
  restaurant_bill: { label: 'Conto ristorante', Icon: UtensilsCrossed },
  experience: { label: 'Esperienza', Icon: Sparkles },
  excursion: { label: 'Escursione', Icon: Compass },
  service: { label: 'Servizio', Icon: Wrench },
  stay: { label: 'Soggiorno', Icon: BedDouble },
  table: { label: 'Tavolo', Icon: UtensilsCrossed },
  product: { label: 'Prodotto', Icon: Package },
};
const kindOf = (k) => KIND[k] || { label: 'Voce', Icon: Sparkles };

/**
 * Mini-carrello a comparsa (drawer a destra) — globale su sito pubblico + Area Clienti.
 * Riusa le API FASE 3 tramite il CartContext, senza duplicare la logica del carrello.
 */
export function MiniCartDrawer() {
  const navigate = useNavigate();
  const {
    cart, preview, count, isCustomer, miniCartOpen, setMiniCartOpen,
    updateItem, removeItem, refresh,
  } = useCart() || {};
  const [busyId, setBusyId] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  const items = cart?.items || [];
  const p = preview || {};

  const changeQty = async (item, delta) => {
    setBusyId(item.item_id);
    try { await updateItem(item.item_id, Number(item.qty || 1) + delta); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusyId(null); }
  };
  const doRemove = async (item) => {
    setBusyId(item.item_id);
    try { await removeItem(item.item_id); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const goToLogin = () => {
    setMiniCartOpen(false);
    navigate('/cliente/accedi?next=/cliente/carrello');
  };
  const goToCart = () => { setMiniCartOpen(false); navigate('/cliente/carrello'); };

  const doCheckout = async () => {
    // Il pagamento avviene SEMPRE nella pagina Carrello con Stripe ufficiale
    // (indirizzo di spedizione per i prodotti + Payment Element sicuro).
    setMiniCartOpen(false);
    navigate('/cliente/carrello');
  };

  return (
    <Sheet open={!!miniCartOpen} onOpenChange={setMiniCartOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="mini-cart-drawer">
        <SheetHeader className="space-y-1 border-b border-border px-5 py-4">
          <SheetTitle className="flex items-center gap-2 font-display">
            <ShoppingCart className="h-5 w-5 text-primary" /> Il tuo carrello
            {isCustomer && count > 0 && <Badge variant="secondary" className="ml-1 tabular-nums">{count}</Badge>}
          </SheetTitle>
          <SheetDescription>Componi la tua Maxi Experience: più operatori, un unico checkout.</SheetDescription>
        </SheetHeader>

        {/* Non loggato */}
        {!isCustomer ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center" data-testid="mini-cart-login-prompt">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground"><LogIn className="h-6 w-6" /></span>
            <p className="text-sm text-muted-foreground">Accedi all'Area Clienti per aggiungere esperienze al carrello e usare i vantaggi Tessera.</p>
            <Button onClick={goToLogin} className="gap-2" data-testid="mini-cart-login-button">Accedi <ArrowRight className="h-4 w-4" /></Button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center" data-testid="mini-cart-empty">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground"><Leaf className="h-6 w-6" /></span>
            <p className="text-sm text-muted-foreground">Il carrello è vuoto. Sfoglia esperienze, escursioni e servizi e aggiungili qui.</p>
            <Button variant="outline" onClick={goToCart} data-testid="mini-cart-browse">Esplora il catalogo</Button>
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
              {items.map((it) => {
                const K = kindOf(it.kind);
                const isStay = it.kind === 'stay';
                const isTable = it.kind === 'table';
                const m = it.meta || {};
                return (
                  <div key={it.item_id} className="flex gap-3" data-testid={`mini-cart-item-${it.item_id}`}>
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                      {it.image ? <img src={mediaUrl(it.image)} alt={it.title} className="h-full w-full object-cover" loading="lazy" />
                        : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><K.Icon className="h-5 w-5" /></div>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{it.title}</p>
                      {isStay ? (
                        <>
                          {m.structure_name && <p className="truncate text-xs text-muted-foreground">{m.structure_name}</p>}
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{fmtD(m.check_in)}–{fmtD(m.check_out)}</span>
                            <span className="inline-flex items-center gap-1"><BedDouble className="h-3 w-3" />{m.nights} nott{m.nights === 1 ? 'e' : 'i'}</span>
                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{m.guests}</span>
                          </p>
                          <div className="mt-1 flex items-center gap-2">
                            <span className="text-xs font-semibold tabular-nums">{eur(it.price)}</span>
                            <HoldCountdown expiresAt={m.hold_expires_at} onExpire={() => refresh(0)} />
                          </div>
                        </>
                      ) : isTable ? (
                        <>
                          {m.restaurant_name && <p className="truncate text-xs text-muted-foreground">{m.restaurant_name}</p>}
                          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                            <span className="inline-flex items-center gap-1"><CalendarDays className="h-3 w-3" />{fmtD(m.date)}</span>
                            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{m.time}</span>
                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{m.people}</span>
                          </p>
                          <p className="mt-1 text-xs font-semibold tabular-nums">{it.price > 0 ? `Caparra ${eur(it.price)}` : 'Prenotazione gratuita'}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-xs text-muted-foreground">{eur(it.price)}</p>
                          {!it.meta?.booking_payload && (<div className="mt-1.5 flex w-fit items-center gap-1 rounded-full border border-border p-0.5">
                            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" disabled={busyId === it.item_id}
                              onClick={() => changeQty(it, -1)} data-testid={`mini-cart-dec-${it.item_id}`} aria-label="Diminuisci"><Minus className="h-3 w-3" /></Button>
                            <span className="w-6 text-center text-xs font-semibold tabular-nums">{it.qty}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full" disabled={busyId === it.item_id}
                              onClick={() => changeQty(it, 1)} data-testid={`mini-cart-inc-${it.item_id}`} aria-label="Aumenta"><Plus className="h-3 w-3" /></Button>
                          </div>)}
                        </>
                      )}
                    </div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      disabled={busyId === it.item_id} onClick={() => doRemove(it)} data-testid={`mini-cart-remove-${it.item_id}`} aria-label="Rimuovi">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <SheetFooter className="flex-col gap-3 border-t border-border px-5 py-4">
              <div className="w-full space-y-1.5">
                <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotale</span><span className="tabular-nums">{eur(p.subtotal)}</span></div>
                {Number(p.tessera_discount) > 0 && (
                  <div className="flex justify-between text-sm text-primary"><span className="flex items-center gap-1"><TicketPercent className="h-3.5 w-3.5" />Sconto Tessera ({p.tier_discount_pct || 0}%)</span><span className="tabular-nums">- {eur(p.tessera_discount)}</span></div>
                )}
                {p.bundle_applied && Number(p.bundle_discount) > 0 && (
                  <div className="flex justify-between text-sm text-primary" data-testid="mini-cart-bundle"><span className="flex items-center gap-1"><TicketPercent className="h-3.5 w-3.5" />Sconto pacchetto ({p.bundle_pct || 0}%)</span><span className="tabular-nums">- {eur(p.bundle_discount)}</span></div>
                )}
                {Number(p.ecom_coupon_total) > 0 && (
                  <div className="flex justify-between text-sm text-primary"><span className="flex items-center gap-1"><TicketPercent className="h-3.5 w-3.5" />Coupon negozio</span><span className="tabular-nums">- {eur(p.ecom_coupon_total)}</span></div>
                )}
                {Number(p.points_redeemed) > 0 && (
                  <div className="flex justify-between text-sm text-primary" data-testid="mini-cart-redeemed"><span className="flex items-center gap-1"><Sparkles className="h-3.5 w-3.5" />Punti riscattati ({n(p.points_redeemed)})</span><span className="tabular-nums">- {eur(p.points_redeemed_eur)}</span></div>
                )}
                {Number(p.charity) > 0 && (
                  <div className="flex items-start justify-between text-sm text-muted-foreground" data-testid="mini-cart-charity">
                    <span className="flex items-center gap-1"><HandHeart className="h-3.5 w-3.5 text-primary" />{p.charity_source === 'surcharge' ? 'Quota Progetto Sila' : 'di cui Progetto Sila'}</span>
                    <span className="tabular-nums">{p.charity_source === 'surcharge' ? `+ ${eur(p.charity)}` : eur(p.charity)}</span>
                  </div>
                )}
                <Separator className="my-1" />
                <div className="flex items-end justify-between"><span className="font-medium">Totale</span><span className="font-display text-xl font-bold tabular-nums" data-testid="mini-cart-total">{eur(p.total)}</span></div>
                {p.tessera_enabled && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" />Guadagni {n(p.points_earned)} punti Tessera</p>
                )}
              </div>
              <div className="grid w-full grid-cols-2 gap-2">
                <Button variant="outline" onClick={goToCart} data-testid="mini-cart-goto-cart">Vai al carrello</Button>
                <Button onClick={doCheckout} disabled={checkingOut} className="gap-1.5" data-testid="mini-cart-checkout">
                  {checkingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}Paga
                </Button>
              </div>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default MiniCartDrawer;

